import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { GridColDef } from '@mui/x-data-grid';
import { DataTable } from '../../shared/components/DataTable/DataTable';
import { SimplePieChart, type PieSlice } from '../../shared/components/SimplePieChart';
import { StatusChip } from '../../shared/components/StatusChip';
import { extractErrorMessage } from '../../core/services/api';
import { resources } from '../../core/services/resources';
import { flagFor } from '../../shared/utils/geo';
import type {
  PromptSetPrompt,
  ReportTemplatePromptSet,
} from '../../core/types/data-models';

/**
 * One synthetic row per prompt that will actually be executed by the
 * Report Template, regardless of whether it was attached directly or via
 * a PromptSet. `source` distinguishes the two so the Source column reads
 * `Direct` or `Set: <name>`.
 *
 * Per-prompt category/persona/stage/brand is computed as
 *   (assignment override) ?? (prompt's own default)
 * exactly mirroring how a run would resolve effective settings. Geo +
 * AI-model overrides live only on the assignment row — the prompt itself
 * has no defaults for those.
 */
interface EffectivePrompt {
  id: string; // stable key for DataGrid: `direct:<rtpId>` or `set:<rtpsId>:<promptId>`
  source: 'direct' | { type: 'set'; setId: number; setName: string };
  /** Numeric FK against `Prompt.id`. */
  promptId: number;
  promptText: string;
  status: string;
  aiModelIds: number[];
  geoLocationIds: number[];
  categoryId: number | null;
  personaId: number | null;
  stageId: number | null;
  brandId: number | null;
}

interface ReportTemplatePreviewTabProps {
  templateId: number;
  // Lookup maps shared with the host page's catalog queries — passed in
  // so we don't refetch them once per tab switch.
  categoryNameById: Map<number, string>;
  personaNameById: Map<number, string>;
  stageNameById: Map<number, string>;
  brandNameById: Map<number, string>;
  promptSetNameById: Map<number, string>;
  promptTextById: Map<number, string>;
  geoById: Map<number, { code: string; name: string }>;
}

export function ReportTemplatePreviewTab({
  templateId,
  categoryNameById,
  personaNameById,
  stageNameById,
  brandNameById,
  promptSetNameById,
  promptTextById,
  geoById,
}: ReportTemplatePreviewTabProps) {
  // ---- direct ReportTemplatePrompts ---------------------------------------
  const directPrompts = useQuery({
    queryKey: ['report-template-prompts', { templateId, all: true }],
    queryFn: () =>
      resources.reportTemplatePrompts.list({
        pageSize: 500,
        filter: { reportTemplateId: templateId },
        sort: '-id',
      }),
  });

  // ---- attached PromptSets ------------------------------------------------
  const promptSets = useQuery({
    queryKey: ['report-template-prompt-sets', { templateId, all: true }],
    queryFn: () =>
      resources.reportTemplatePromptSets.list({
        pageSize: 500,
        filter: { reportTemplateId: templateId },
        sort: '-id',
      }),
  });

  // For each attached PromptSet, fetch the prompts that belong to it.
  // `useQueries` runs them in parallel and gives us an array of results.
  const setIds = useMemo<number[]>(
    () => (promptSets.data?.data ?? []).map((s) => s.promptSetId),
    [promptSets.data],
  );

  const setPromptsQueries = useQueries({
    queries: setIds.map((setId) => ({
      queryKey: ['prompt-sets', setId, 'prompts', 'preview'],
      queryFn: () =>
        resources.promptSetPromptsFor(setId).list({ pageSize: 500 }),
    })),
  });

  // ---- AI model catalog ---------------------------------------------------
  // The aggregate table maps `AIModel.id` → display name + identifier.
  // Cached under the same key the dialogs use so it's already warm on this tab.
  const aiModels = useQuery({
    queryKey: ['ai-models', 'select'],
    queryFn: () => resources.aiModels.list({ pageSize: 500, sort: 'aiEngine' }),
  });
  const aiModelById = useMemo(() => {
    const map = new Map<number, { name: string; identifier: string }>();
    for (const m of aiModels.data?.data ?? []) {
      map.set(m.id, { name: m.name, identifier: m.aiModelIdentifier });
    }
    return map;
  }, [aiModels.data]);

  // ---- merge into the list of effective prompt rows -----------------------
  const merged = useMemo<EffectivePrompt[]>(() => {
    const rows: EffectivePrompt[] = [];

    // Direct prompts — one row each, override falls back to prompt-side
    // defaults are handled by the prompt's own defaultX fields, but for
    // direct rows the assignment IS the authoritative source.
    for (const rtp of directPrompts.data?.data ?? []) {
      rows.push({
        id: `direct:${rtp.id}`,
        source: 'direct',
        promptId: rtp.promptId,
        promptText: promptTextById.get(rtp.promptId) ?? `#${rtp.promptId}`,
        status: rtp.status,
        aiModelIds: rtp.aiModelIds ?? [],
        geoLocationIds: rtp.geoLocationIds ?? [],
        categoryId: rtp.categoryId,
        personaId: rtp.personaId,
        stageId: rtp.stageId,
        brandId: rtp.brandId,
      });
    }

    // Via-set prompts — for each set assignment, expand its member prompts.
    // The set assignment's overrides apply uniformly to every member.
    const setAssignmentByPromptSetId = new Map<number, ReportTemplatePromptSet>();
    for (const a of promptSets.data?.data ?? []) {
      setAssignmentByPromptSetId.set(a.promptSetId, a);
    }

    setIds.forEach((setId, index) => {
      const setAssignment = setAssignmentByPromptSetId.get(setId);
      if (!setAssignment) return;
      const setName = promptSetNameById.get(setId) ?? `#${setId}`;
      const setPrompts = setPromptsQueries[index]?.data?.data ?? [];

      for (const row of setPrompts as PromptSetPrompt[]) {
        // The PromptSetPrompt junction includes the related `prompt` row
        // (default include) so we can read its defaultX fields without an
        // extra round-trip. Cast through `unknown` because the mirrored
        // type doesn't model the eager include.
        const prompt = (row as PromptSetPrompt & { prompt?: { id: number; defaultCategoryId: number | null; defaultPersonaId: number | null; defaultStageId: number | null; defaultBrandId: number | null } }).prompt;
        if (!prompt) continue;
        rows.push({
          id: `set:${setAssignment.id}:${prompt.id}`,
          source: { type: 'set', setId, setName },
          promptId: prompt.id,
          promptText: promptTextById.get(prompt.id) ?? `#${prompt.id}`,
          status: setAssignment.status,
          aiModelIds: setAssignment.aiModelIds ?? [],
          geoLocationIds: setAssignment.geoLocationIds ?? [],
          // Assignment override > prompt default. Same precedence as runs.
          categoryId: setAssignment.categoryId ?? prompt.defaultCategoryId ?? null,
          personaId: setAssignment.personaId ?? prompt.defaultPersonaId ?? null,
          stageId: setAssignment.stageId ?? prompt.defaultStageId ?? null,
          brandId: setAssignment.brandId ?? prompt.defaultBrandId ?? null,
        });
      }
    });

    return rows;
  }, [
    directPrompts.data,
    promptSets.data,
    setIds,
    setPromptsQueries,
    promptSetNameById,
    promptTextById,
  ]);

  // ---- aggregates ---------------------------------------------------------
  const categoryStats = useGroupCounts(merged, (r) => r.categoryId, categoryNameById);
  const personaStats = useGroupCounts(merged, (r) => r.personaId, personaNameById);
  const stageStats = useGroupCounts(merged, (r) => r.stageId, stageNameById);

  // AI models / geo locations are arrays — each prompt contributes one
  // count to every model / country it would target. So if prompt P is run
  // against [GPT, Claude] in [US, IL], that's 1 per cell across both
  // tables (not 2×2).
  const aiModelStats = useMemo<AggregateRow[]>(() => {
    const counts = new Map<number, number>();
    for (const row of merged) {
      for (const id of row.aiModelIds) counts.set(id, (counts.get(id) ?? 0) + 1);
    }
    return [...counts.entries()]
      .map(([id, count]) => {
        const model = aiModelById.get(id);
        return {
          key: String(id),
          label: model?.name ?? `#${id}`,
          sub: model?.identifier ?? '',
          count,
        };
      })
      .sort((a, b) => b.count - a.count);
  }, [merged, aiModelById]);

  const geoStats = useMemo<AggregateRow[]>(() => {
    const counts = new Map<number, number>();
    for (const row of merged) {
      for (const id of row.geoLocationIds) counts.set(id, (counts.get(id) ?? 0) + 1);
    }
    return [...counts.entries()]
      .map(([id, count]) => {
        const geo = geoById.get(id);
        return {
          key: String(id),
          label: geo ? `${flagFor(geo.code)} ${geo.name}` : `#${id}`,
          sub: geo?.code ?? '',
          count,
        };
      })
      .sort((a, b) => b.count - a.count);
  }, [merged, geoById]);

  // ---- table columns ------------------------------------------------------
  const columns: GridColDef<EffectivePrompt>[] = useMemo(
    () => [
      {
        field: 'source',
        headerName: 'Source',
        width: 200,
        valueGetter: (_v, row) => (row.source === 'direct' ? 'Direct' : `Set: ${row.source.setName}`),
        renderCell: (p) => {
          const r = p.row;
          if (r.source === 'direct') {
            return <Typography variant="body2" color="text.secondary">Direct</Typography>;
          }
          return (
            <Typography variant="body2" color="primary" noWrap title={r.source.setName}>
              Set: {r.source.setName}
            </Typography>
          );
        },
      },
      {
        field: 'promptText',
        headerName: 'Prompt',
        flex: 1,
        minWidth: 280,
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 110,
        renderCell: (p) => <StatusChip value={p.value as string} />,
      },
      {
        field: 'aiModelIds',
        headerName: 'AI models',
        width: 200,
        renderCell: (p) => {
          const ids = (p.value as number[] | null | undefined) ?? [];
          if (ids.length === 0) return '';
          return ids.map((id) => aiModelById.get(id)?.name ?? `#${id}`).join(', ');
        },
      },
      {
        field: 'geoLocationIds',
        headerName: 'Geo',
        width: 220,
        renderCell: (p) => {
          const ids = (p.value as number[] | null | undefined) ?? [];
          if (ids.length === 0) return '';
          return ids
            .map((id) => {
              const geo = geoById.get(id);
              return geo ? `${flagFor(geo.code)} ${geo.code}` : `#${id}`;
            })
            .join(', ');
        },
      },
      {
        field: 'categoryId',
        headerName: 'Category',
        width: 160,
        renderCell: (p) => lookup(p.value, categoryNameById),
      },
      {
        field: 'personaId',
        headerName: 'Persona',
        width: 140,
        renderCell: (p) => lookup(p.value, personaNameById),
      },
      {
        field: 'stageId',
        headerName: 'Stage',
        width: 160,
        renderCell: (p) => lookup(p.value, stageNameById),
      },
      {
        field: 'brandId',
        headerName: 'Brand',
        width: 160,
        renderCell: (p) => lookup(p.value, brandNameById),
      },
    ],
    [
      aiModelById,
      brandNameById,
      categoryNameById,
      geoById,
      personaNameById,
      stageNameById,
    ],
  );

  const errorMessage = [directPrompts, promptSets]
    .filter((q) => q.isError)
    .map((q) => extractErrorMessage(q.error))[0];

  const loading =
    directPrompts.isFetching ||
    promptSets.isFetching ||
    setPromptsQueries.some((q) => q.isFetching);

  return (
    <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      {/* Statistics block — collapsible, default expanded. */}
      <Accordion defaultExpanded disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight={600}>
            Report template statistics
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
            {merged.length} effective prompt{merged.length === 1 ? '' : 's'} (direct + via prompt sets)
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {/* Aggregate tables first: the counts users most often scan for
              ("how many prompts will run on each AI model / in each
              country") read top-to-bottom without scrolling. */}
          <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            <AggregateTable title="AI models" empty="No AI models assigned." rows={aiModelStats} />
            <AggregateTable title="Geo locations" empty="No countries assigned." rows={geoStats} />
          </Stack>
          {/* Categorical breakdowns below — visual summary of how the
              effective prompts are spread across category / stage / persona. */}
          <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
            <ChartCard title="By category" data={categoryStats} />
            <ChartCard title="By stage" data={stageStats} />
            <ChartCard title="By persona" data={personaStats} />
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* The merged prompts table — flex-fills the remaining vertical space. */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <DataTable<EffectivePrompt>
          rows={merged}
          columns={columns}
          loading={loading}
          paginationMode="client"
          emptyMessage="No prompts attached yet."
        />
      </Box>
    </Stack>
  );
}

// -----------------------------------------------------------------------------
// helpers
// -----------------------------------------------------------------------------

interface AggregateRow {
  key: string;
  label: string;
  sub?: string;
  count: number;
}

function useGroupCounts(
  rows: EffectivePrompt[],
  pick: (r: EffectivePrompt) => number | null,
  nameById: Map<number, string>,
): PieSlice[] {
  return useMemo(() => {
    const counts = new Map<number | 'unset', number>();
    for (const r of rows) {
      const k = pick(r);
      const key: number | 'unset' = k == null ? 'unset' : k;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    const slices: PieSlice[] = [];
    for (const [k, v] of counts) {
      const label = k === 'unset' ? '(unset)' : nameById.get(k) ?? `#${k}`;
      slices.push({ label, value: v });
    }
    return slices;
  }, [rows, pick, nameById]);
}

function ChartCard({ title, data }: { title: string; data: PieSlice[] }) {
  return (
    <Paper sx={{ p: 2, minWidth: 320 }}>
      <Typography variant="subtitle2" gutterBottom>
        {title}
      </Typography>
      <SimplePieChart data={data} size={160} />
    </Paper>
  );
}

function AggregateTable({
  title,
  empty,
  rows,
}: {
  title: string;
  empty: string;
  rows: AggregateRow[];
}) {
  if (rows.length === 0) {
    return (
      <Paper sx={{ p: 2, minWidth: 320 }}>
        <Typography variant="subtitle2" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {empty}
        </Typography>
      </Paper>
    );
  }
  return (
    <Paper sx={{ minWidth: 320, flex: '1 1 320px' }}>
      <Box sx={{ p: 2, pb: 1 }}>
        <Typography variant="subtitle2">{title}</Typography>
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell align="right" sx={{ width: 100 }}>
                Prompts
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.key}>
                <TableCell>
                  <Stack>
                    <Typography variant="body2">{r.label}</Typography>
                    {r.sub && (
                      <Typography variant="caption" color="text.secondary">
                        {r.sub}
                      </Typography>
                    )}
                  </Stack>
                </TableCell>
                <TableCell align="right">{r.count}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

function lookup(value: unknown, map: Map<number, string>): string {
  const id = value as number | null | undefined;
  if (id == null) return '';
  return map.get(id) ?? `#${id}`;
}
